/**
 * Native FCM Push Notifications wiring — Android 16 / SDK 36 hardened.
 *
 * NOTE on imports: we use a STATIC top-level import for the plugin instead of
 * a dynamic `await import(...)`. In Capacitor's `file://` WebView origin,
 * dynamic webpack chunks sometimes hang indefinitely (observed on Android 16),
 * which made our "Enable Notifications" button timeout. Static import bundles
 * the plugin into the main JS chunk so it loads with the page.
 */
import { PushNotifications } from "@capacitor/push-notifications";
import { isNativePlatform } from "./platform";
import { api } from "./api";

let _initialized = false;
const NATIVE_TIMEOUT_MS = 8000;

function withTimeout(promise, label, ms = NATIVE_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`[push] ${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

/**
 * Initialise push. Safe to call repeatedly.
 *
 * Android 16 (SDK 36) note: the @capacitor/push-notifications@7 plugin has a
 * stale checkPermissions() reading on Android 16 — it returns "denied" even
 * after the user enables notifications in OS Settings. So we do NOT gate on
 * the JS-side permission check. We attempt requestPermissions() (which on the
 * device opens the system popup or no-ops if already granted) and then ALWAYS
 * call register(). Real errors surface via the `registrationError` event.
 */
export async function initPushNotifications({ onDeepLink, verbose = true } = {}) {
  if (!isNativePlatform()) return false;
  try {
    // Static import — no chunk-load wait.
    // Attach listeners ONCE.
    if (!_initialized) {
      PushNotifications.addListener("registration", async (token) => {
        try {
          await api.post("/devices/register", {
            token: token.value,
            platform: "android",
            app_version: "1.0.0",
          });
          if (verbose) console.info("[push] backend registered:", token.value.slice(0, 20) + "…");
          try {
            const { toast } = await import("sonner");
            toast.success("Notifications enabled ✓");
          } catch {/* ignore */}
        } catch (e) {
          console.warn("[push] backend register failed", e?.response?.status, e?.response?.data || e?.message);
          try {
            const { toast } = await import("sonner");
            toast.error("Backend registration fail (login dobara karein)", { duration: 6000 });
          } catch {/* ignore */}
        }
      });
      PushNotifications.addListener("registrationError", async (err) => {
        console.warn("[push] registrationError", err);
        try {
          const { toast } = await import("sonner");
          const msg = String(err?.error || err?.message || err || "");
          if (/permiss/i.test(msg) || /denied/i.test(msg) || /SERVICE_NOT_AVAILABLE/i.test(msg)) {
            toast.error("Notification permission OS level pe denied hai. Phone Settings → Apps → HisaabPro → Notifications → Allow karein.", { duration: 10000 });
          } else {
            toast.error(`Registration error: ${msg.slice(0, 120)}`, { duration: 8000 });
          }
        } catch {/* ignore */}
      });
      PushNotifications.addListener("pushNotificationReceived", (notif) => {
        try { window.dispatchEvent(new CustomEvent("hp:push", { detail: notif })); } catch {/* ignore */}
      });
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const path = action?.notification?.data?.path;
        if (path && typeof onDeepLink === "function") {
          try { onDeepLink(path); } catch (e) { console.warn("deep-link nav failed:", e); }
        }
      });
      _initialized = true;
    }

    // Best-effort prompt — does NOT gate the register call. On Android 16 the
    // plugin's check can return stale "denied" even when OS Settings show
    // notifications enabled; calling requestPermissions() either no-ops or
    // re-prompts. Either way we then attempt register().
    try {
      const reqPerm = await withTimeout(PushNotifications.requestPermissions(), "requestPermissions", 30000);
      if (verbose) console.info("[push] requestPermissions →", reqPerm);
    } catch (e) {
      if (verbose) console.info("[push] requestPermissions threw (continuing):", e?.message);
    }

    // Always call register(). Real outcome comes through the listeners above.
    await withTimeout(PushNotifications.register(), "PushNotifications.register");
    if (verbose) console.info("[push] register() invoked — waiting for registration event…");
    return true;
  } catch (e) {
    console.warn("[push] init failed:", e?.message || e);
    try {
      const { toast } = await import("sonner");
      toast.error(`Push init failed: ${e?.message || e}`, { duration: 8000 });
    } catch {/* ignore */}
    return false;
  }
}

/**
 * Unregister listeners. Fire-and-forget — never blocks logout/teardown.
 */
export function unregisterPushNotifications() {
  if (!isNativePlatform()) return;
  // Non-blocking on purpose. We have seen the Capacitor bridge hang on
  // Android 16 here, which used to freeze the logout flow.
  (async () => {
    try {
      await withTimeout(PushNotifications.removeAllListeners(), "removeAllListeners", 2000);
      _initialized = false;
    } catch (e) {
      console.warn("[push] unregister failed (non-fatal):", e?.message || e);
    }
  })();
}
